
import React, { useState, useEffect, useMemo } from 'react';
import { Line } from '../types';
import Pagination from './Pagination';
import { ITEMS_PER_PAGE, PencilIcon, TrashIcon } from '../constants';
import { useDatabaseContext } from '../contexts/DatabaseContext';

interface LineTableProps {
  lines: Line[];
  onModify: (line: Line) => void;
  onDelete: (line: Line) => void;
}

const LineTable: React.FC<LineTableProps> = ({ lines, onModify, onDelete }) => {
  const { apiUrl } = useDatabaseContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [systemDefaults, setSystemDefaults] = useState({
    conveyorStartDelay: 1000,
    conveyorStopDelay: 2000,
    bufferHopperOpenDelay: 500,
    bufferHopperCloseDelay: 1500,
    weighingDelay: 3000,
    zeroingDelay: 1000,
    zeroThresholdWeight: 0.1,
    hopperCapacity: 50.0,
    defaultInfeedTargetWeight: 10.0,
    // Arm timing defaults
    armTimingSettings: [] as any[],
    // Outfeed defaults
    outfeedHopperCapacity: 50.0,
    outfeedCycleWaitTime: 2000,
    outfeedWeighDelay: 3000,
    outfeedZeroDelay: 1000,
    outfeedZeroThresholdWeight: 0.1,
    outfeedBreakoutAttempts: 3,
    outfeedPocketBufferHopperCloseDelay: 1500,
  });
  const totalPages = Math.ceil(lines.length / ITEMS_PER_PAGE);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const lastPageIndex = firstPageIndex + ITEMS_PER_PAGE;
    return lines.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, lines]);

  // Fetch system defaults when component mounts
  useEffect(() => {
    const fetchDefaults = async () => {
      try {
        const response = await fetch(`${apiUrl}/system-defaults`);
        if (response.ok) {
          const data = await response.json();
          const defaults = data.data;
          const armTimingSettings = defaults.armTimingSettings ? 
            (typeof defaults.armTimingSettings === 'string' ? 
              JSON.parse(defaults.armTimingSettings) : 
              defaults.armTimingSettings) : [];
              
          setSystemDefaults({
            conveyorStartDelay: defaults.conveyorStartDelay,
            conveyorStopDelay: defaults.conveyorStopDelay,
            bufferHopperOpenDelay: defaults.bufferHopperOpenDelay,
            bufferHopperCloseDelay: defaults.bufferHopperCloseDelay,
            weighingDelay: defaults.weighingDelay,
            zeroingDelay: defaults.zeroingDelay,
            zeroThresholdWeight: defaults.zeroThresholdWeight,
            hopperCapacity: defaults.hopperCapacity,
            defaultInfeedTargetWeight: defaults.defaultInfeedTargetWeight,
            // Arm timing settings
            armTimingSettings: armTimingSettings,
            // Outfeed defaults
            outfeedHopperCapacity: defaults.outfeedHopperCapacity || 50.0,
            outfeedCycleWaitTime: defaults.outfeedCycleWaitTime || 2000,
            outfeedWeighDelay: defaults.outfeedWeighDelay || 3000,
            outfeedZeroDelay: defaults.outfeedZeroDelay || 1000,
            outfeedZeroThresholdWeight: defaults.outfeedZeroThresholdWeight || 0.1,
            outfeedBreakoutAttempts: defaults.outfeedBreakoutAttempts || 3,
            outfeedPocketBufferHopperCloseDelay: defaults.outfeedPocketBufferHopperCloseDelay || 1500,
          });
        }
      } catch (error) {
        console.error('Error fetching system defaults:', error);
      }
    };
    fetchDefaults();
  }, [apiUrl]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };


  if (lines.length === 0) {
    return <p className="text-slate-600 italic text-center py-8">No lines configured.</p>;
  }

  return (
    <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] text-sm text-left text-slate-600">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50">
            <tr>
              <th scope="col" className="px-4 py-3">Hardware ID</th>
              <th scope="col" className="px-4 py-3">Line Name</th>
              <th scope="col" className="px-4 py-3 text-center">Stations</th>
              <th scope="col" className="px-4 py-3 text-center">Pockets</th>
              <th scope="col" className="px-4 py-3 text-center">Infeed Settings</th>
              <th scope="col" className="px-4 py-3 text-center">Outfeed Settings</th>
              <th scope="col" className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTableData.map((line) => {
              // Parse line's arm timing settings
              const lineArmTiming = line.armTimingSettings ? 
                (typeof line.armTimingSettings === 'string' ? 
                  JSON.parse(line.armTimingSettings) : 
                  line.armTimingSettings) : [];
              
              // Check if arm timing settings are different from defaults
              const hasCustomArmTiming = () => {
                if (lineArmTiming.length !== systemDefaults.armTimingSettings.length) {
                  return true;
                }
                return lineArmTiming.some((timing: any, index: number) => {
                  const defaultTiming = systemDefaults.armTimingSettings[index];
                  if (!defaultTiming) return true;
                  return timing.armOpenDelay !== defaultTiming.armOpenDelay || 
                         timing.armOpenDuration !== defaultTiming.armOpenDuration;
                });
              };
              
              // Compare line settings against system defaults to determine if custom
              const hasCustomInfeedSettings = line.conveyorStartDelay !== systemDefaults.conveyorStartDelay || 
                line.conveyorStopDelay !== systemDefaults.conveyorStopDelay || 
                line.bufferHopperOpenDelay !== systemDefaults.bufferHopperOpenDelay ||
                line.bufferHopperCloseDelay !== systemDefaults.bufferHopperCloseDelay ||
                line.weighingDelay !== systemDefaults.weighingDelay ||
                line.zeroingDelay !== systemDefaults.zeroingDelay ||
                line.zeroThresholdWeight !== systemDefaults.zeroThresholdWeight ||
                line.hopperCapacity !== systemDefaults.hopperCapacity ||
                line.defaultInfeedTargetWeight !== systemDefaults.defaultInfeedTargetWeight ||
                hasCustomArmTiming();

              const hasCustomOutfeedSettings = line.outfeedHopperCapacity !== (systemDefaults.outfeedHopperCapacity || 50.0) ||
                line.outfeedCycleWaitTime !== (systemDefaults.outfeedCycleWaitTime || 2000) ||
                line.outfeedWeighDelay !== (systemDefaults.outfeedWeighDelay || 3000) ||
                line.outfeedZeroDelay !== (systemDefaults.outfeedZeroDelay || 1000) ||
                line.outfeedZeroThresholdWeight !== (systemDefaults.outfeedZeroThresholdWeight || 0.1) ||
                line.outfeedBreakoutAttempts !== (systemDefaults.outfeedBreakoutAttempts || 3) ||
                line.outfeedPocketBufferHopperCloseDelay !== (systemDefaults.outfeedPocketBufferHopperCloseDelay || 1500);
              
              return (
                <tr key={line.id} className="bg-white border-b border-slate-200 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{line.hardwareId || '-'}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{line.name}</td>
                  <td className="px-4 py-3 text-center">{line.numberOfStations}</td>
                  <td className="px-4 py-3 text-center">{line.numberOfProductPockets}</td>
                  <td className="px-4 py-3 text-center">
                    <span 
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        hasCustomInfeedSettings 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}
                      title={hasCustomInfeedSettings ? 'Custom infeed settings configured' : 'Using default infeed settings'}
                    >
                      {hasCustomInfeedSettings ? 'Custom' : 'Default'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span 
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        hasCustomOutfeedSettings 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}
                      title={hasCustomOutfeedSettings ? 'Custom outfeed settings configured' : 'Using default outfeed settings'}
                    >
                      {hasCustomOutfeedSettings ? 'Custom' : 'Default'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                  <div className="flex justify-center space-x-3">
                    <button
                      onClick={() => onModify(line)}
                      className="p-3 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg border border-indigo-200 hover:border-indigo-300 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                      style={{ minHeight: '44px', minWidth: '44px' }}
                      title="Modify Line"
                      aria-label={`Modify line ${line.name}`}
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onDelete(line)}
                      className="p-3 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg border border-red-200 hover:border-red-300 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                      style={{ minHeight: '44px', minWidth: '44px' }}
                      title="Delete Line"
                      aria-label={`Delete line ${line.name}`}
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default LineTable;
